'use client'

import { useTranslation } from 'react-i18next';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';
import { usePolicy } from '@/context/PolicyContext';
import { 
  Home, 
  Plane, 
  MapPin, 
  Bell, 
  User, 
  Settings,
  Trophy,
  Activity,
  Menu,
  Users,
  FileText,
  BarChart3,
  UserCog,
  Shield,
  TowerControl,
  ClipboardPaste,
  Calendar,
  Globe,
  PlaneIcon,
  Sun,
  Moon,
  Monitor,
  Check,
  Map,
  Medal,
  TrendingUp,
  Search
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useNotifications } from '@/context/NotificationsContext';
import { useTheme } from '@/components/theme-provider';
import { type LucideIcon } from "lucide-react"
import { useSearch } from '@/context/SearchContext';

interface NavItem {
  icon: LucideIcon;
  label: string;
  href?: string;
  action?: () => void;
  active: boolean;
  badge?: number;
  permission?: string;
  isMore?: boolean;
}

interface MoreNavItem {
  icon: LucideIcon;
  label: string;
  href?: string;
  action?: () => void;
  active: boolean;
  badge?: number;
  permission?: string;
}

const MobileBottomNav = () => {
  const { t } = useTranslation();
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuth();
  const { hasPermission } = usePolicy();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const { openNotifications, unreadCount } = useNotifications();
  const { theme, setTheme } = useTheme();

  const { open: searchOpen, setOpen: setSearchOpen } = useSearch();

  const primaryNavItems = [
    {
      icon: Home,
      label: t('navigation.overview'),
      href: `/${user?.username}`,
      active: pathname === `/${user?.username}`,
    },
    {
      icon: Plane,
      label: t('navigation.flights'),
      href: `/${user?.username}/flights`,
      active: pathname.includes('/flights') && !pathname.includes('/admin'),
    },
    {
      icon: Bell,
      label: t('navigation.notifications'),
      action: openNotifications,
      active: false,
      badge: unreadCount > 0 ? unreadCount : undefined,
    },
    {
      icon: Menu,
      label: t('common.more'),
      href: '#',
      active: false,
      isMore: true,
    },
  ];

  const moreNavItems: Array<{section: string, items: MoreNavItem[]}> = [
    // Community Section
    {
      section: 'Dispatch',
      items: [
        {
          icon: FileText,
          label: t('navigation.contracts'),
          href: `/dispatch/contracts`,
          active: pathname.includes('/contracts'),
        },
        {
          icon: ClipboardPaste,
          label: t('navigation.route_planner'),
          href: '/dispatch/route-planner',
          active: pathname.includes('/dispatch/route-planner'),
        },
        {
          icon: TowerControl,
          label: t('navigation.focus_airports'),
          href: '/dispatch/focus-airports',
          active: pathname === '/dispatch/focus-airports',
        },
        {
          icon: Map,
          label: t('navigation.tours'),
          href: '/dispatch/tours',
          active: pathname === '/dispatch/tours',
        },
      ]
    },
    {
      section: 'Community',
      items: [
        {
          icon: Activity,
          label: t('navigation.activity'),
          href: '/community/activity',
          active: pathname.includes('/activity'),
        },
        {
          icon: Globe,
          label: t('navigation.worldmap'),
          href: '/community/world-map',
          active: pathname.includes('/world-map'),
        },
        {
          icon: Medal,
          label: t('navigation.leaderboard'),
          href: '/community/leaderboard',
          active: pathname.includes('/leaderboard'),
        },
        {
          icon: Calendar,
          label: t('navigation.events'),
          href: '/community/events',
          active: pathname.includes('/events') && !pathname.includes('/admin'),
        },
      ]
    },
    // Settings & Profile
    {
      section: t('navigation.settings'),
      items: [
        {
          icon: Search,
          label: t('common.search'),
          action: () => setSearchOpen(true),
          active: !!searchOpen
        },
        {
          icon: User,
          label: t('settings.profile.title'),
          href: '/settings?tab=profile',
          active: pathname.includes('/settings') && !pathname.includes('/admin'),
        },
        {
            icon: Settings,
            label: t('settings.title'),
            href: '/settings',
            active: pathname.includes('/settings') && !pathname.includes('/admin'),
        }
      ]
    },
    // Theme Settings
    {
      section: t('settings.theme'),
      items: [
        {
          icon: Sun,
          label: t('settings.lightmode'),
          action: () => setTheme('light'),
          active: theme === 'light',
        },
        {
          icon: Moon,
          label: t('settings.darkmode'),
          action: () => setTheme('dark'),
          active: theme === 'dark',
        },
        {
          icon: Monitor,
          label: t('settings.system'),
          action: () => setTheme('system'),
          active: theme === 'system',
        }
      ]
    },
  ];

  // Add admin section if user has permissions
  if (user?.is_admin || hasPermission('view-admin')) {
    moreNavItems.push({
      section: 'Admin',
      items: [
        {
          icon: Shield,
          label: t('admin.overview.title'),
          href: '/admin',
          active: pathname === '/admin',
        },
        {
          icon: TrendingUp,
          label: t('admin.statistics.title'),
          href: '/admin/statistics',
          active: pathname.includes('/admin/statistics'),
          permission: 'view-users',
        },
        {
          icon: PlaneIcon,
          label: t('admin.flights.manage_flights'),
          href: '/admin/flights',
          active: pathname.includes('/admin/flights'),
          permission: 'view-flights',
        },
        {
          icon: Users,
          label: t('admin.users.manage_users'),
          href: '/admin/users',
          active: pathname.includes('/admin/users'),
          permission: 'view-users',
        },
        {
            icon: TowerControl,
            label: t('admin.focus_airports.manage_focus_week'),
            href: '/admin/focus-airports',
            active: pathname.includes('/admin/focus-airports'),
            permission: 'manage-focus-airports',
        },
        {
          icon: UserCog,
          label: t('admin.roles.title'),
          href: '/admin/roles',
          active: pathname.includes('/admin/roles'),
          permission: 'view-roles',
        },
        {
          icon: Calendar,
          label: t('admin.events.manage_events'),
          href: '/admin/events',
          active: pathname.includes('/admin/events'),
          permission: 'view-events',
        },
      ].filter(item => !item.permission || user?.is_admin || hasPermission(item.permission)) as MoreNavItem[]
    });
  }

  const handleNavClick = (href?: string, action?: () => void) => {
    if (action) {
      action();
      setIsMoreOpen(false);
      return;
    }
    if (href && href !== '#') {
      router.push(href);
      setIsMoreOpen(false);
    }
  };

  const handleMoreClick = () => {
    setIsMoreOpen(true);
  };

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border md:hidden">
        <div className="grid grid-cols-4 h-22">
          {primaryNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.href || item.label}
                onClick={() => {
                  if (item.isMore) {
                    handleMoreClick();
                  } else if (item.action) {
                    item.action();
                  } else if (item.href) {
                    handleNavClick(item.href);
                  }
                }}
                className={cn(
                  "flex flex-col items-center justify-center h-full transition-colors relative",
                  "hover:bg-accent hover:text-accent-foreground",
                  item.active 
                    ? "text-primary bg-accent" 
                    : "text-muted-foreground"
                )}
              >
                <div className="relative">
                  <Icon className="h-5 w-5 mb-1" />
                  {item.badge && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1 text-[14px]">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span className="text-xs font-medium truncate px-1">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <Sheet open={isMoreOpen} onOpenChange={setIsMoreOpen}>
        <SheetContent side="bottom" className="h-[70vh] z-[9999] rounded-t-lg">
          <SheetHeader>
            <SheetTitle>{t('navigation.title')}</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-6 overflow-y-auto max-h-[calc(70vh-100px)]">
            {moreNavItems.map((section) => (
              <div key={section.section}>
                <h3 className="font-semibold text-sm text-muted-foreground mb-3">
                  {section.section}
                </h3>
                <div className="space-y-2">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Button
                        key={item.href || item.label}
                        variant={item.active ? "secondary" : "ghost"}
                        className="w-full justify-start h-12 relative"
                        onClick={() => handleNavClick(item.href, item.action)}
                      >
                        <Icon className="h-5 w-5 mr-3" />
                        {item.label}
                        {item.active && section.section === t('settings.theme') && (
                          <Check className="h-4 w-4 ml-auto" />
                        )}
                        {item.badge && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </Button>
                    );
                  })}
                </div>
                {section !== moreNavItems[moreNavItems.length - 1] && (
                  <Separator className="mt-4" />
                )}
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileBottomNav;